import React, { Component } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faEllipsisH
} from "@fortawesome/free-solid-svg-icons";

import styles from "./Pagination.scss";

class Pagination extends Component {
  constructor(props) {
    super(props);

    this.state = {
      allowPrevious: false,
      allowNext: false
    };

    const { total, itemsPerPage } = this.props;
    this.totalPages = Math.ceil((total ? total : 0) / itemsPerPage);
    this.paginationItems = [];
  }

  componentDidMount() {
    this.setupPaginationItems();
  }

  componentDidUpdate(prevProps) {
    if (this.props.currentPage !== prevProps.currentPage) {
      this.setupPaginationItems();
    }
  }

  setupPaginationItems = () => {
    let pagination = [];
    let { currentPage } = this.props;
    let jumpPrevNavShown = false;
    let jumpNextNavShown = false;
    let _pageNumber;
    let highestIndex;
    // To keep current page centered, number of items visible = # items to the left / right of current page
    // ie : numberOfItemsVisible = 1 on page 9 would become [8] [9] [10]
    // ie : numberOfItemsVisible = 2 on page 9 would become [7] [8] [9] [10] [11]
    let numberOfItemsVisible = this.props.paginationOffset;

    for (let pageNumber = 1; pageNumber <= this.totalPages; pageNumber++) {
      // Always display First and Last page
      if (pageNumber === 1 || pageNumber === this.totalPages) {
        pagination.push({
          type: "firstLast",
          number: pageNumber,
          label: pageNumber
        });
        continue;
      }

      // Generate Jump Previous Pagination Icon
      // Current Page - Item to the Left of Current Page has to be bigger than 3 (Page 1 + Jump Prev Icon + Page 2)
      if (
        pageNumber === 2 &&
        !jumpPrevNavShown &&
        currentPage > 3 + numberOfItemsVisible
      ) {
        _pageNumber = pageNumber;
        if (currentPage - 2 > 2) {
          _pageNumber = currentPage - 2;
        }

        pagination.push({
          type: "ellipsis",
          number: _pageNumber,
          label: (
            <FontAwesomeIcon className="fa-layers fa-fw" icon={faEllipsisH} />
          )
        });

        jumpPrevNavShown = true;
        continue;
      }

      // Generate Middle Range Pagination
      let diffDecrease = numberOfItemsVisible;

      // Bottom range modifier
      if (!jumpPrevNavShown && currentPage <= 2 + 2 * numberOfItemsVisible) {
        diffDecrease = 1 + numberOfItemsVisible;
      } else if (pageNumber > this.totalPages - 2 * numberOfItemsVisible - 3) {
        // Top Bound - Calculate what should be displayed below current page
        diffDecrease = currentPage + numberOfItemsVisible + 1;
      }

      let diffIncrease = numberOfItemsVisible;
      if (
        !jumpPrevNavShown &&
        pageNumber - numberOfItemsVisible < 4 + numberOfItemsVisible
      ) {
        diffIncrease = pageNumber + numberOfItemsVisible;
      } else if (this.totalPages - currentPage <= 2 + numberOfItemsVisible) {
        // Bottom Bound - Calculate what should be displayed above current page
        diffIncrease = numberOfItemsVisible + 2;
      }

      let lowerRange = currentPage - diffDecrease;

      let higherRange = currentPage + diffIncrease;

      if (pageNumber >= lowerRange && pageNumber <= higherRange) {
        highestIndex = pageNumber;
        pagination.push({
          type: "page",
          number: pageNumber,
          label: pageNumber
        });
        continue;
      }

      // Generate Jump Next Pagination Icon
      // Current Page + Item to the Right of Current Page has to be bigger than Total Page - 2 (Jump Next Icon + Last Page)
      if (
        pageNumber === this.totalPages - 1 &&
        !jumpNextNavShown &&
        currentPage + numberOfItemsVisible < this.totalPages - 2
      ) {
        _pageNumber = pageNumber;
        if (highestIndex + numberOfItemsVisible < this.totalPages - 1) {
          _pageNumber = highestIndex + numberOfItemsVisible;
        }
        pagination.push({
          type: "ellipsis",
          number: _pageNumber,
          label: (
            <FontAwesomeIcon className="fa-layers fa-fw" icon={faEllipsisH} />
          )
        });

        jumpNextNavShown = true;
      }
    }
    this.handlePrevNextPageLogic((this.paginationItems = pagination));
  };

  // Toggle Prev / Next page button based on current page
  handlePrevNextPageLogic = callback => {
    const { currentPage } = this.props;
    let _allowPrevious = false;
    let _allowNext = false;

    if (currentPage > 1) {
      _allowPrevious = true;
    }

    if (currentPage < this.totalPages) {
      _allowNext = true;
    }

    this.setState(
      {
        allowPrevious: _allowPrevious,
        allowNext: _allowNext
      },
      () => {
        if (callback && typeof callback === "function") {
          callback();
        }
      }
    );
  };

  handlePrevPage = () => {
    const { currentPage, handlePagination } = this.props;
    this.handlePrevNextPageLogic(handlePagination(currentPage - 1));
  };

  handleNextPage = () => {
    const { currentPage, handlePagination } = this.props;
    this.handlePrevNextPageLogic(handlePagination(currentPage + 1));
  };

  handlePage = page => {
    const { handlePagination } = this.props;
    this.handlePrevNextPageLogic(handlePagination(page));
  };

  render() {
    // Set localized text
    let _prevPage, _nextPage;
    switch (this.props.lang.toLowerCase()) {
      case "fr":
        _prevPage = "Page précédente";
        _nextPage = "Page suivante";
        break;
      case "es":
        _prevPage = "Pagina anterior";
        _nextPage = "Siguiente página";
        break;
      default:
        _prevPage = "Previous Page";
        _nextPage = "Next Page";
    }

    return (
      <ul className={`swg-news-pagination ${styles.pagination}`}>
        <li
          title={_prevPage}
          className={`swg-news-pagination-prev ${styles.paginationItem}`}
          aria-disabled={!this.state.allowPrevious}
        >
          <button
            className={`swg-news-pagination-item-button ${
              styles.paginationItemBtn
            } ${styles.prevBtn}`}
            onClick={this.handlePrevPage}
            disabled={!this.state.allowPrevious}
          >
            <FontAwesomeIcon className="fa-layers fa-fw" icon={faChevronLeft} />
          </button>
        </li>
        {this.paginationItems.map((pagination, index) => {
          let _styleModifier = "";
          if (pagination.type !== "page") {
            _styleModifier = `swg-news-pagination-item--${pagination.type}`;
          }
          return (
            <li
              key={index}
              className={`swg-news-pagination-item ${_styleModifier} ${
                styles.paginationItem
              }`}
            >
              <button
                className={`swg-news-pagination-item-button ${
                  styles.paginationItemBtn
                } ${
                  this.props.currentPage === pagination.number
                    ? `swg-news-pagination-item-button--active ${
                        styles.active
                      } `
                    : ""
                }`}
                onClick={() => this.handlePage(pagination.number)}
              >
                {pagination.label}
              </button>
            </li>
          );
        })}
        <li
          title={_nextPage}
          className={`swg-news-pagination-prev ${styles.paginationItem}`}
          aria-disabled={!this.state.allowNext}
        >
          <button
            className={`swg-news-pagination-item-button ${
              styles.paginationItemBtn
            } ${styles.nextBtn}`}
            onClick={this.handleNextPage}
            disabled={!this.state.allowNext}
          >
            <FontAwesomeIcon
              className="fa-layers fa-fw"
              icon={faChevronRight}
            />
          </button>
        </li>
      </ul>
    );
  }
}

export default Pagination;
